import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import History from '@mui/icons-material/History';
import axios from 'axios';
import { url } from '../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { roleLabels } from '../utils/constants';

export default function LogsList({ usePageTitle }) {
  usePageTitle?.('PixTick | Logs de alterações');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    async function fetchLogs() {
      try {
        setError(null);
        const token = localStorage.getItem('token');
        const { data } = await axios.get(url + '/api/v1/user/logs', {
          headers: { Authorization: 'Bearer ' + token },
          params: { limit: 100 }
        });
        setLogs(Array.isArray(data?.data) ? data.data : []);
      } catch (err) {
        if (err.response?.status === 401) {
          logout();
          navigate('/signin');
        } else {
          setError('Erro ao carregar logs.');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchLogs();
  }, [navigate, logout]);

  const fieldLabel = (field) => {
    const map = { role: 'Nível', fullname: 'Nome', empresa: 'Empresa', cnpj: 'CNPJ', numero: 'Telefone' };
    return map[field] || field;
  };

  const formatValue = (value, field) => {
    if (value == null || value === '') return '—';
    if (field === 'role') return roleLabels[value] || value;
    return String(value);
  };

  return (
    <Container sx={{ marginTop: '80px', pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976D2', display: 'flex', alignItems: 'center', gap: 1 }}>
          <History /> Logs de alterações
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Histórico de alterações em perfis de usuários.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : logs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'grey.50', borderRadius: 2 }}>
          <History sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Nenhum registro de alteração</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#1976D2' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Data/Hora</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Usuário</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Campo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Valor antigo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Valor novo</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Alterado por</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, idx) => (
                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    {log.createdAt
                      ? new Date(log.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>{log.userEmail || '—'}</TableCell>
                  <TableCell>{fieldLabel(log.field)}</TableCell>
                  <TableCell>{formatValue(log.oldValue, log.field)}</TableCell>
                  <TableCell>{formatValue(log.newValue, log.field)}</TableCell>
                  <TableCell>{log.changedByNome || log.changedByEmail || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
